import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { formatDate } from "../../../utils/date.utils";
import apiClient from "../../../services/api";
import { useToast } from "../../../contexts/ToastContext";
import { isSafeUrl } from "../../../utils/url-validation";
import {
  articleRoleLabels,
  articleRoleColors,
} from "../shared/articleRoleLabels";
import { ArticleAttachmentList } from "../shared/ArticleAttachmentList";
import type { ArticleAttachment } from "../shared/article-attachment.types";

export interface Article {
  mappingId: string;
  mappingType: string;
  id: string;
  title: string;
  url: string;
  publishedAt: string | null;
  articleRole: string;
  attachments?: ArticleAttachment[];
}

interface CallArticlesListProps {
  articles: Article[];
  canWrite?: boolean;
  callId?: string;
}

// articleRoleLabels and articleRoleColors imported from shared/articleRoleLabels.ts

interface DeleteConfirmModalProps {
  isOpen: boolean;
  articleTitle: string;
  isDeleting: boolean;
  errorMessage?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation modal for article removal from a call.
 */
const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  articleTitle,
  isDeleting,
  errorMessage,
  onConfirm,
  onCancel,
}) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Focus cancel button when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => cancelButtonRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isDeleting) {
        onCancel();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, isDeleting, onCancel]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="delete-confirm-title"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={isDeleting ? undefined : onCancel}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2
              id="delete-confirm-title"
              className="text-lg font-medium text-neutral-900"
            >
              Usuń artykuł z naboru
            </h2>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-sm text-neutral-700">
              Czy na pewno chcesz usunąć powiązanie z artykułem:
            </p>
            <p className="mt-2 text-sm font-medium text-neutral-900 break-words">
              &ldquo;{articleTitle}&rdquo;
            </p>
            <p className="mt-3 text-sm text-neutral-500">
              Artykuł sam w sobie nie zostanie usunięty — tylko powiązanie z tym
              naborem.
            </p>
          </div>

          {/* Error message */}
          {errorMessage && (
            <div className="px-6 pb-2">
              <div
                className="p-3 bg-red-50 border border-red-200 rounded-md"
                role="alert"
              >
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
            <button
              ref={cancelButtonRef}
              type="button"
              onClick={onCancel}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isDeleting && (
                <span
                  className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                  aria-hidden="true"
                />
              )}
              {isDeleting ? "Usuwanie..." : "Usuń"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Component displaying a list of articles related to a grant call.
 * When canWrite is true and callId is provided, shows delete buttons per article.
 */
const CallArticlesList: React.FC<CallArticlesListProps> = ({
  articles,
  canWrite = false,
  callId,
}) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [deletingMappingId, setDeletingMappingId] = useState<string | null>(
    null,
  );
  const [confirmArticle, setConfirmArticle] = useState<Article | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (mappingId: string) => {
      await apiClient.delete(
        `/admin/grants/calls/${callId}/articles/${mappingId}`,
      );
    },
    onSuccess: () => {
      setConfirmArticle(null);
      setDeletingMappingId(null);
      setDeleteError(null);
      if (callId) {
        queryClient.invalidateQueries({ queryKey: ["grants-call", callId] });
      }
      addToast({
        message: "Artykuł został usunięty z naboru",
        type: "success",
      });
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      setDeletingMappingId(null);
      const status = error?.response?.status;
      if (status === 404) {
        // Mapping already gone - refresh the list anyway
        setConfirmArticle(null);
        setDeleteError(null);
        if (callId) {
          queryClient.invalidateQueries({ queryKey: ["grants-call", callId] });
        }
      } else if (status && status >= 500) {
        setDeleteError("Błąd serwera. Spróbuj ponownie później.");
      } else if (!error?.response) {
        setDeleteError("Błąd połączenia. Sprawdź połączenie z internetem.");
      } else {
        setDeleteError("Wystąpił nieoczekiwany błąd.");
      }
    },
  });

  if (!articles || articles.length === 0) {
    return null;
  }

  const handleDeleteClick = (e: React.MouseEvent, article: Article) => {
    e.stopPropagation();
    setDeleteError(null);
    setConfirmArticle(article);
  };

  const handleConfirmDelete = () => {
    if (!confirmArticle || !callId) return;
    setDeletingMappingId(confirmArticle.mappingId);
    deleteMutation.mutate(confirmArticle.mappingId);
  };

  const handleCancelDelete = () => {
    if (!deleteMutation.isPending) {
      setConfirmArticle(null);
      setDeletingMappingId(null);
      setDeleteError(null);
    }
  };

  // NOTE: this call-detail list links to the raw article URL directly, unlike
  // the main /grants/articles browse list which routes through the anti-scraping
  // redirect proxy. Intentional: a single call's article set is small (a handful
  // of links), so it is not a bulk-harvest surface worth proxying here.
  const handleArticleClick = (url: string) => {
    if (url && isSafeUrl(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <>
      <ul className="divide-y divide-neutral-200">
        {articles.map((article) => (
          <li
            key={article.mappingId}
            className="px-6 py-4 hover:bg-neutral-50 cursor-pointer"
            onClick={() => handleArticleClick(article.url)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleArticleClick(article.url);
              }
            }}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 min-w-0">
                {/* Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    className="h-5 w-5 text-neutral-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                    />
                  </svg>
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-medium text-neutral-900 truncate">
                      {article.title}
                    </h3>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        articleRoleColors[article.articleRole] ||
                        articleRoleColors.OTHER
                      }`}
                      title={
                        articleRoleLabels[article.articleRole] ||
                        article.articleRole
                      }
                    >
                      {articleRoleLabels[article.articleRole] ||
                        article.articleRole}
                    </span>
                    {article.mappingType === "MANUAL" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800">
                        Ręcznie dodane
                      </span>
                    )}
                  </div>
                  {article.publishedAt && (
                    <p className="mt-0.5 text-xs text-neutral-500 text-left">
                      {formatDate(article.publishedAt)}
                    </p>
                  )}

                  {/* Attachments considered during LLM processing */}
                  <ArticleAttachmentList
                    attachments={article.attachments ?? []}
                    articleTitle={article.title}
                  />
                </div>
              </div>

              {/* Right-side actions */}
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                {/* Open link */}
                {article.url && isSafeUrl(article.url) && (
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                  >
                    <svg
                      className="w-3.5 h-3.5 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    Otwórz
                  </a>
                )}

                {/* Delete button (admin only) */}
                {canWrite && callId && (
                  <button
                    type="button"
                    onClick={(e) => handleDeleteClick(e, article)}
                    disabled={deletingMappingId === article.mappingId}
                    className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-neutral-100 rounded disabled:opacity-50 transition-colors"
                    title="Usuń artykuł z naboru"
                    aria-label={`Usuń artykuł "${article.title}" z naboru`}
                  >
                    {deletingMappingId === article.mappingId ? (
                      <span
                        className="inline-block w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"
                        aria-hidden="true"
                      />
                    ) : (
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <DeleteConfirmModal
        isOpen={confirmArticle !== null}
        articleTitle={confirmArticle?.title ?? ""}
        isDeleting={deleteMutation.isPending}
        errorMessage={deleteError}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

export default CallArticlesList;
